import React, { useState } from "react";
import { Box, Typography, Button, Grid, Chip, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import VerifiedIcon from "@mui/icons-material/Verified";
import SecurityIcon from "@mui/icons-material/Security";
import BoltIcon from "@mui/icons-material/Bolt";

const TRUST_BADGES = [
  { icon: "🏦", label: "RBI Certified" },
  { icon: "🔒", label: "256-bit SSL" },
  { icon: "⚡", label: "Instant Transfer" },
  { icon: "🇮🇳", label: "Made in India" },
];

const STATS = [
  { value: "10k+", label: "Transactions", color: "#16a34a" },
  { value: "₹500Cr+", label: "Processed", color: "#0ea5e9" },
  { value: "99.9%", label: "Uptime", color: "#7c3aed" },
  { value: "15+", label: "BBPS Services", color: "#f59e0b" },
];

export default function BannerSection() {
  const navigate = useNavigate();

  return (
    <Box sx={{
      pt: { xs: 10, md: 12 },
      pb: { xs: 6, md: 0 },
      overflow: "hidden",
      position: "relative",
      background: "linear-gradient(160deg, #f0fdf4 0%, #ffffff 40%, #f0f9ff 100%)",
      minHeight: { md: "100vh" },
      display: "flex",
      alignItems: "center",
    }}>
      {/* Background blobs */}
      <Box sx={{ position: "absolute", top: "5%", right: "-5%", width: { xs: 200, md: 420 }, height: { xs: 200, md: 420 }, borderRadius: "50%", background: "radial-gradient(circle, rgba(22,163,74,0.12) 0%, transparent 70%)", pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: "10%", left: "-8%", width: { xs: 150, md: 320 }, height: { xs: 150, md: 320 }, borderRadius: "50%", background: "radial-gradient(circle, rgba(14,165,233,0.10) 0%, transparent 70%)", pointerEvents: "none" }} />

      <Box sx={{ width: "100%", px: { xs: 3, md: 8, lg: 12 } }}>
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
          {/* Left: Copy */}
          <Grid size={{xs:12, md:6}} item xs={12} md={6}>
            <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, ease: "easeOut" }}>
              <Chip
                icon={<VerifiedIcon sx={{ fontSize: 14, color: "#16a34a !important" }} />}
                label="India's Most Trusted Digital Wallet"
                sx={{ bgcolor: "#dcfce7", color: "#15803d", fontWeight: 700, fontSize: 12, mb: 3, px: 1, border: "1px solid #bbf7d0" }}
              />

              <Typography sx={{
                fontSize: { xs: 36, sm: 48, md: 56, lg: 64 },
                fontWeight: 900, color: "#0f172a",
                lineHeight: 1.08, letterSpacing: "-2px", mb: 1,
              }}>
                One App.
              </Typography>
              <Typography sx={{
                fontSize: { xs: 36, sm: 48, md: 56, lg: 64 },
                fontWeight: 900, lineHeight: 1.08, letterSpacing: "-2px", mb: 2,
                background: "linear-gradient(135deg, #16a34a 0%, #22c55e 60%, #86efac 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>
                All Payments.
              </Typography>
              <Typography sx={{ fontSize: { xs: 16, md: 18 }, color: "#64748b", lineHeight: 1.75, mb: 4, maxWidth: 480 }}>
                Pay bills, recharge, send money and manage your RuPay card — all in one secure, RBI-compliant digital wallet.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={5}>
                <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />}
                  sx={{
                    background: "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)",
                    color: "white", fontWeight: 700, fontSize: 16,
                    px: 4, py: 1.7, borderRadius: "12px",
                    boxShadow: "0 8px 28px rgba(22,163,74,0.35)",
                    "&:hover": { background: "linear-gradient(135deg, #15803d 0%, #16a34a 100%)", boxShadow: "0 12px 36px rgba(22,163,74,0.45)", transform: "translateY(-1px)" },
                    transition: "all 0.2s",
                  }}>
                  Discover Wallet 

                </Button>
                <Button variant="outlined" size="large"
                  onClick={() => navigate("/login")}
                  sx={{
                    borderColor: "#cbd5e1", color: "#374151", fontWeight: 600,
                    fontSize: 15, px: 3.5, py: 1.7, borderRadius: "12px",
                    "&:hover": { borderColor: "#16a34a", color: "#16a34a", bgcolor: "#f0fdf4" }                                                                                                                                
                  }}>
                  Sign In
                </Button>
              </Stack>

              {/* Trust badges */}
              <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1}>
                {TRUST_BADGES.map((b) => (
                  <Box key={b.label} sx={{ display: "flex", alignItems: "center", gap: 0.7, bgcolor: "white", border: "1px solid #e2e8f0", borderRadius: "8px", px: 1.5, py: 0.8, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                    <Typography sx={{ fontSize: 14 }}>{b.icon}</Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>{b.label}</Typography>
                  </Box>
                ))}
              </Stack>
            </motion.div>
          </Grid>

          {/* Right: App Mockup */}
          <Grid size={{xs:12, md:6}} item xs={12} md={6} sx={{ display: "flex", justifyContent: "center", position: "relative" }}>
            <motion.div initial={{ opacity: 0, y: 40, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}>
              <Box sx={{ position: "relative", maxWidth: 380 }}>
                {/* Phone glow */}
                <Box sx={{ position: "absolute", inset: -40, background: "radial-gradient(ellipse, rgba(22,163,74,0.15) 0%, transparent 70%)", pointerEvents: "none" }} />

                {/* App card */}
                <Box sx={{
                  bgcolor: "white", borderRadius: "32px",
                  boxShadow: "0 32px 80px rgba(0,0,0,0.12), 0 0 0 1px rgba(22,163,74,0.1)",
                  overflow: "hidden", position: "relative",
                }}>
                  {/* Wallet card inside */}
                  <Box sx={{
                    background: "linear-gradient(135deg, #0f4c25 0%, #16a34a 50%, #22c55e 100%)",
                    p: 3, position: "relative", overflow: "hidden",
                  }}>
                    <Box sx={{ position: "absolute", top: -20, right: -20, width: 120, height: 120, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.08)" }} />
                    <Box sx={{ position: "absolute", bottom: -30, left: 20, width: 90, height: 90, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.05)" }} />
                    <Box sx={{ position: "relative", zIndex: 1 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2.5 }}>
                        <Box>
                          <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.7)"}}></Typography>
                          <Typography sx={{ fontSize: 30, fontWeight: 900, color: "white", letterSpacing: "-1px" , mt: 3 }}>₹48,250</Typography>
                        </Box>
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.15)", borderRadius: "10px", p: 1 }}>
                          <Box component="img" src="/Assets/ta_walletlogo.png" sx={{ height: 24, filter: "brightness(0) invert(1)" }} onError={(e)=>{e.target.style.display="none";}} />
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.8)", fontWeight: 500 }}>Rahul Kumar · Full KYC</Typography>
                        <Chip label="● ACTIVE" size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontSize: 10, fontWeight: 700, height: 20 }} />
                      </Box>
                    </Box>
                  </Box>

                  {/* Quick actions */}
                  <Box sx={{ p: 2.5 }}>
                    <Grid container spacing={1.5} mb={2.5}>
                      {[
                        { icon: "💸", label: "Send", color: "#f0fdf4" },
                        { icon: "⚡", label: "Recharge", color: "#fef9f0" },
                        { icon: "🏦", label: "Bank", color: "#f0f9ff" },
                        { icon: "📱", label: "Bills", color: "#faf5ff" },
                      ].map((a) => (
                        <Grid size={{xs:3}} item xs={3} key={a.label}>
                          <Box sx={{ textAlign: "center" }}>
                            <Box sx={{ width: 44, height: 44, borderRadius: "14px", bgcolor: a.color, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 0.5, fontSize: 20 }}>
                              {a.icon}
                            </Box>
                            <Typography sx={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>{a.label}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Recent tx */}
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.5px" }}>Recent Activity</Typography>
                    {[
                      { name: "Electricity Bill", amount: "-₹1,840", time: "2h ago", icon: "⚡", positive: false },
                      { name: "Mobile Recharge", amount: "-₹599", time: "Yesterday", icon: "📱", positive: false },
                      { name: "Money Received", amount: "+₹5,000", time: "2d ago", icon: "💚", positive: true },
                    ].map((tx, i) => (
                      <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, borderBottom: i < 2 ? "1px solid #f8fafc" : "none" }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: "10px", bgcolor: tx.positive ? "#f0fdf4" : "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                          {tx.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{tx.name}</Typography>
                          <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>{tx.time}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: tx.positive ? "#16a34a" : "#0f172a" }}>{tx.amount}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Floating stats */}
                <Box sx={{ position: "absolute", top: -16, left: -24, bgcolor: "white", borderRadius: "14px", px: 2, py: 1.5, boxShadow: "0 8px 24px rgba(0,0,0,0.1)", display: { xs: "none", md: "block" } }}>
                  <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>TRANSACTIONS TODAY</Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 900, color: "#16a34a" }}>₹12,450</Typography>
                </Box>

                <Box sx={{ position: "absolute", bottom: 40, right: -28, bgcolor: "white", borderRadius: "14px", px: 2, py: 1.5, boxShadow: "0 8px 24px rgba(0,0,0,0.1)", display: { xs: "none", md: "block" } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SecurityIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                    <Box>
                      <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>PAYMENT SECURED</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>256-bit SSL</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>

        {/* Stats bar */}
        <Box sx={{ mt: { xs: 6, md: 10 }, py: 4, borderTop: "1px solid #f1f5f9" }}>
          <Grid container spacing={2}>
            {STATS.map((s, i) => (
              <Grid size={{xs:6, md:3}} item xs={6} md={3} key={i}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 900, color: s.color, letterSpacing: "-1px" }}>{s.value}</Typography>
                  <Typography sx={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>{s.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
